import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * Custom cursor — a small dot with a trailing ring.
 * The ring enlarges on interactive elements (links, buttons).
 * Auto-disables on touch devices.
 */
export default function Cursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [isTouch, setIsTouch] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (window.matchMedia("(hover: none)").matches) {
            setIsTouch(true);
            return;
        }
        let raf;
        const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        const ring = { x: target.x, y: target.y };

        const onMove = (e) => {
            target.x = e.clientX;
            target.y = e.clientY;
            if (!visible) setVisible(true);
            if (dotRef.current) {
                dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
            }
        };
        const onLeave = () => setVisible(false);
        const onEnter = () => setVisible(true);

        const onOver = (e) => {
            const t = e.target;
            if (
                t.closest &&
                t.closest("a, button, input, textarea, select, [role='button'], [data-cursor='hover']")
            ) {
                setHovering(true);
            } else {
                setHovering(false);
            }
        };

        const tick = () => {
            ring.x += (target.x - ring.x) * 0.16;
            ring.y += (target.y - ring.y) * 0.16;
            if (ringRef.current) {
                ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0)`;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseover", onOver);
        window.addEventListener("mouseleave", onLeave);
        window.addEventListener("mouseenter", onEnter);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
            window.removeEventListener("mouseleave", onLeave);
            window.removeEventListener("mouseenter", onEnter);
        };
    }, [visible]);

    if (isTouch) return null;

    return (
        <>
            <motion.div
                ref={dotRef}
                data-testid="custom-cursor-dot"
                initial={{ opacity: 0 }}
                animate={{ opacity: visible ? 1 : 0 }}
                transition={{ duration: 0.25 }}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    pointerEvents: "none",
                    zIndex: 9999,
                    marginLeft: -3,
                    marginTop: -3,
                    width: 6,
                    height: 6,
                    background: "var(--terra)",
                    borderRadius: "50%",
                    mixBlendMode: "multiply",
                }}
            />
            <motion.div
                ref={ringRef}
                data-testid="custom-cursor-ring"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{
                    opacity: visible ? (hovering ? 0.9 : 0.5) : 0,
                    scale: hovering ? 1.9 : 1,
                }}
                transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    pointerEvents: "none",
                    zIndex: 9998,
                    marginLeft: -18,
                    marginTop: -18,
                    width: 36,
                    height: 36,
                    border: "1px solid var(--forest-dark)",
                    borderRadius: "50%",
                }}
            />
        </>
    );
}
